import { useEffect, type FC, type ReactNode } from 'react'
import { StatsigProvider, useStatsigClient, useStatsigUser } from '@statsig/react-bindings'
import { observer } from 'mobx-react-lite'
import { useEntitiesContext } from 'entities/EntitiesExport'

const StatsigExperimentListener: FC = () => {
  const entities = useEntitiesContext()
  const { client } = useStatsigClient()

  useEffect(() => {
    const listener = (event: {
      experiment: { name: string; groupName: string | null; value: Record<string, unknown> }
    }) => {
      if (!event.experiment.groupName) return
      entities.eventLogStore.logEvent('Experiment Viewed', {
        experiment_id: event.experiment.name,
        variation_id: event.experiment.groupName,
        variation_name: event.experiment.value?.modal_version,
      })
    }

    client.on('experiment_evaluation', listener)

    return () => client.off('experiment_evaluation', listener)
  }, [client, entities.eventLogStore])

  return null
}

const toStatsigTier = (env: string | undefined): string => {
  if (env === 'local') return 'development'
  if (env === 'qa') return 'staging'
  return env ?? 'production'
}

// The Statsig client is constructed once from its `initialUser` and is NOT
// rebuilt when the `user` prop changes (see @statsig/react-bindings
// useStatsigInternalClientFactoryAsync — memoized with empty deps). So once the
// authenticated user id becomes available we must promote it imperatively via
// updateUserAsync rather than relying on the prop.
const StatsigUserSync: FC = observer(() => {
  const { usersStore } = useEntitiesContext()
  const userId = usersStore.user?.id
  const { updateUserAsync } = useStatsigUser()

  useEffect(() => {
    if (!userId) return
    void updateUserAsync({ userID: String(userId), custom: { surface: 'web_app' } })
  }, [userId, updateUserAsync])

  return null
})

type Props = { children: ReactNode }

// StatsigProvider is always mounted at a stable position so `children` (the
// entire RouterProvider subtree) never change parent element type. Previously
// this branched between `<>{children}</>` (pre-auth) and
// `<StatsigProvider>{children}</StatsigProvider>` (post-auth); when the user id
// populated, the changed wrapper type forced React to unmount and remount the
// whole router tree, re-running the full bootstrap (auth/refresh, getAppData,
// Twilio device init) a second time. See SWR-23754.
export const StatsigAppProvider: FC<Props> = observer(({ children }) => {
  const { usersStore } = useEntitiesContext()
  const userId = usersStore.user?.id

  return (
    <StatsigProvider
      sdkKey={process.env.STATSIG_CLIENT_KEY ?? ''}
      user={userId ? { userID: String(userId), custom: { surface: 'web_app' } } : { userID: '' }}
      options={{ environment: { tier: toStatsigTier(process.env.REACT_APP_ENV) } }}
    >
      <StatsigUserSync />
      {userId ? <StatsigExperimentListener /> : null}
      {children}
    </StatsigProvider>
  )
})
